import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { get, post, put } from "../../../../api";
import {
  getSiteAssets,
  getSiteDetailsById,
  getSites,
  getUsers,
} from "../../../../store/thunk/site";
import { Autocomplete, TextField } from "@mui/material";
import { formatDate } from "../../../../utils/dateFormat";
import { v4 as uuidv4 } from 'uuid';
import { saveAs } from 'file-saver';
import pdfTemplate from './pdf/Intruder Alarm.pdf';
import RiskScoreCard from "./RiskScoreCard";
import moment from "moment";
import axios from "axios";
import SiteCheckEngineerSelector from "./shared/SiteCheckEngineerSelector";
import SiteCheckEngineerSignature from "./shared/SiteCheckEngineerSignature";
import useSiteCheckEngineers from "./shared/useSiteCheckEngineers";
import {
  getUkLocalDate,
  isCurrentUkInspectionDate,
  toJavaLocalDateTime,
  toJavaLocalDate,
} from "./shared/siteCheckDateUtils";
import SiteCheckDueSummary from "./shared/SiteCheckDueSummary";
import SiteCheckBackButton from "./shared/SiteCheckBackButton";
import { getSiteCheckErrorMessage } from "./shared/siteCheckErrorMessage";
import { calculateSiteCheckDueDate } from "../../../../utils/siteCheckRecurrence";
import { recordGenericInspectionHistory } from "./shared/genericInspectionHistory";

let PDFLib;

if (typeof window !== 'undefined') {
  import('pdf-lib').then((pdfLib) => {
    PDFLib = pdfLib;
  });
}

// Helper function to fetch PDF as ArrayBuffer
const fetchPdfTemplate = async () => {
  try {
    const response = await fetch(pdfTemplate);
    if (!response.ok) {
      throw new Error('Failed to load PDF template: ' + response.statusText);
    }
    const arrayBuffer = await response.arrayBuffer();
    const header = new Uint8Array(arrayBuffer, 0, 5);
    const headerStr = String.fromCharCode.apply(null, header);
    if (headerStr !== '%PDF-') {
      throw new Error('Invalid PDF file: Missing PDF header');
    }
    return arrayBuffer;
  } catch (error) {
    console.error('Error loading PDF template:', error);
    throw new Error('Failed to load PDF template: ' + error.message);
  }
};

const IntruderAlarmCertificate = ({
                                    sasToken,
                                    checkId,
                                    subType,
                                    category,
                                    siteCheck,
                                    siteDetailsById,
                                    getSiteDetailsById,
                                    siteAssets,
                                    getSiteAssets,
                                    users,
                                    getUsers,
                                    siteSelectedForGlobal = {},
                                    loggedInUserData,
                                    embedded = false,
                                  }) => {
  const [formData, setFormData] = useState({
    address: "",
    assetId: "",
    siteContact: "",
    inspectionDate: getUkLocalDate(),
    siteContactNo: "",
    job: "",
    report: "",
    param1: "", // jobComplete
    param2: "", // partsRequired
    param3: "", // walkTestComplete
    param4: "", // pirsCleaned
    param5: "", // remoteSignallingCheck
    param6: "", // systemOperationCheck
    param7: "", // audibleWarningCheck
    param8: "", // electricalConnectionsCheck

    client: "",
    user: loggedInUserData || {},
    engineer: loggedInUserData?.id || "",
    selectedAsset: null,
    signedDate: getUkLocalDate(),
    clientUser: null,
    siteContactUser: null,
    actionId: null,
  });

  const sites = useSelector((state) => state.site.sites);

  // NEW: Use the exact Site Check site, matching Air Conditioning.
  const authoritativeSiteId = siteCheck?.siteId
    ? Number(siteCheck.siteId)
    : Number(siteSelectedForGlobal?.siteId) || null;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [pendingHistoryRetry, setPendingHistoryRetry] = useState(null);
  const [isRetryingHistory, setIsRetryingHistory] = useState(false);
  const [folderIds, setFolderIds] = useState({
    logBooks: null,
    plantAndEquipment: null,
    miscellaneousService: null,
    intruderAlarmService: null
  });
  const [checkStatus, setCheckStatus] = useState('Open');
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [currentCheckId, setCurrentCheckId] = useState(checkId || null);
  const [lastEngineerId, setLastEngineerId] = useState(null);
  const [siteCheckDetails, setSiteCheckDetails] = useState(null);
  const [showRiskAssessment, setShowRiskAssessment] = useState(false);
  const [actionRaised, setActionRaised] = useState(false);
  const [existingAction, setExistingAction] = useState(null);
  const navigate = useNavigate();

  // NEW: Shared engineer behaviour copied from the approved Air Conditioning form.
  const {
    engineerOptions,
    selectedEngineer,
    isLoadingEngineers,
    engineerLoadError,
  } = useSiteCheckEngineers({
    users,
    getUsers,
    siteId: authoritativeSiteId,
    loggedInUserData,
    status: checkStatus,
    selectedEngineerId: formData.engineer,
    selectedEngineerUser: formData.user,
    lastEngineerId,
    leadEngineerId: siteCheck?.leadUserID,
  });

  // NEW: Open checks start with the logged-in engineer and current UK date.
  // Done checks are restored from the saved inspection record.
  useEffect(() => {
    setLastEngineerId(null);
    setFormData((prev) => ({
      ...prev,
      engineer: siteCheck?.status === "Done" ? "" : (loggedInUserData?.id || ""),
      user: siteCheck?.status === "Done" ? {} : (loggedInUserData || {}),
      inspectionDate: siteCheck?.status === "Open" ? getUkLocalDate() : prev.inspectionDate,
      signedDate: siteCheck?.status === "Open" ? getUkLocalDate() : prev.signedDate,
    }));
  }, [checkId, authoritativeSiteId, siteCheck?.status, loggedInUserData?.id]);

  const [popup, setPopup] = useState({
    show: false,
    content: "",
    position: { x: 0, y: 0 },
  });

  const handleMouseEnter = (e, content) => {
    if (!content) return;

    setPopup({
      show: true,
      content,
      position: {
        x: e.target.getBoundingClientRect().left,
        y: e.target.getBoundingClientRect().top - 10,
      },
    });
  };

  const handleMouseLeave = () => {
    setPopup((prev) => ({ ...prev, show: false }));
  };

  const isInternalUserTaggedWithSite =
      (loggedInUserData?.userType === "Internal" || loggedInUserData?.userType === "External") &&
      loggedInUserData?.taggedSites?.some(
          (site) => Number(site.id ?? site.siteId) === authoritativeSiteId
      );

  const selectedAsset = siteAssets.find(
      (asset) => asset.assetId === formData.assetId
  );

  const fetchInspectionData = async (siteCheckStatus = checkStatus) => {
    try {
      if (!checkId) return;

      if (isInternalUserTaggedWithSite && users.length === 0) {
        await getUsers();
      }

      const apiData = await get(`/api/site-check/generic-inspection/${checkId}`);
      if (apiData && apiData.length > 0) {
        const mostRecentItem = apiData[apiData.length - 1];
        setLastEngineerId(mostRecentItem.engineer || null);

        const isCurrentOpenInspection =
          siteCheckStatus === "Open" &&
          isCurrentUkInspectionDate(mostRecentItem.inspectionDate);
        const selectedAsset = siteAssets.find(
            (asset) => asset.assetId === mostRecentItem.assetId
        );

        const clientUser = users.find(
            (user) => user.id === mostRecentItem.client
        );
        const engineerUser = users.find(
            (user) => user.id === mostRecentItem.engineer
        );
        const siteContactUser = users.find(
            (user) => user.id === mostRecentItem.siteContact
        );

        // Fetch action data if actionId exists
        let existingAction = null;
        if (mostRecentItem.actionId) {
          existingAction = await fetchActionById(mostRecentItem.actionId);
          if (existingAction) {
            setExistingAction(existingAction);
            setActionRaised(true);
          }
        }

        setFormData((prev) => ({
          ...prev,
          address: prev.address,
          assetId: mostRecentItem.assetId || prev.assetId,
          siteContact: mostRecentItem.siteContact || prev.siteContact,
          // OLD: inspectionDate: mostRecentItem.inspectionDate || prev.inspectionDate,
          // NEW: Open = current UK date; Done = saved historical date.
          inspectionDate:
            siteCheckStatus === "Open"
              ? getUkLocalDate()
              : (mostRecentItem.inspectionDate || prev.inspectionDate),
          siteContactNo: mostRecentItem.siteContactNo || prev.siteContactNo,
          job: mostRecentItem.job || prev.job,
          report: mostRecentItem.report || prev.report,
          param1: mostRecentItem.param1 || prev.param1,
          param2: mostRecentItem.param2 || prev.param2,
          param3: mostRecentItem.param3 || prev.param3,
          param4: mostRecentItem.param4 || prev.param4,
          param5: mostRecentItem.param5 || prev.param5,
          param6: mostRecentItem.param6 || prev.param6,
          param7: mostRecentItem.param7 || prev.param7,
          param8: mostRecentItem.param8 || prev.param8,

          client: mostRecentItem.client || "",
          // OLD:
          // engineer: mostRecentItem.engineer || prev.engineer || loggedInUserData?.id,
          // user: engineerUser || loggedInUserData || prev.user,
          // NEW: same Open/Done engineer behaviour as Air Conditioning.
          engineer:
            siteCheckStatus === "Open"
              ? (isCurrentOpenInspection
                  ? (mostRecentItem.engineer || loggedInUserData?.id || "")
                  : (loggedInUserData?.id || ""))
              : (mostRecentItem.engineer || prev.engineer || ""),
          user:
            siteCheckStatus === "Open"
              ? (isCurrentOpenInspection
                  ? (engineerUser || loggedInUserData || {})
                  : (loggedInUserData || {}))
              : (engineerUser || prev.user || {}),
          selectedAsset: selectedAsset || prev.selectedAsset,
          // OLD: signedDate: mostRecentItem.signedDate || prev.signedDate,
          // NEW: Open = current UK date; Done = saved historical date.
          signedDate:
            siteCheckStatus === "Open"
              ? getUkLocalDate()
              : (mostRecentItem.signedDate || prev.signedDate),
          clientUser: clientUser || null,
          siteContactUser: siteContactUser || null,
          actionId: mostRecentItem.actionId || null,
        }));
      }
    } catch (error) {
      console.error("Error fetching inspection data:", error);
      toast.error("Failed to load inspection data");
    }
  };

  const fetchActionById = async (id) => {
    try {
      if (!id) return null;
      const response = await get(`/api/site/actions/id/${id}`);
      return response;
    } catch (error) {
      console.error("Error fetching action:", error);
      return null;
    }
  };

  const fetchExistingActions = async () => {
    try {
      if (formData.actionId) {
        const action = await fetchActionById(formData.actionId);
        if (action && action.checkId === currentCheckId) {
          setExistingAction(action);
          setActionRaised(true);
          return;
        }
        setFormData(prev => ({ ...prev, actionId: null }));
      }

      if (!authoritativeSiteId || !currentCheckId) return;

      const response = await get(`/api/site/actions/${authoritativeSiteId}`);
      if (response && response.length > 0) {
        const relevantActions = response.filter(action =>
            action.checkId === currentCheckId
        );

        if (relevantActions.length > 0) {
          const mostRecentAction = relevantActions.sort((a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
          )[0];

          setExistingAction(mostRecentAction);
          setActionRaised(true);

          setFormData(prev => ({
            ...prev,
            actionId: mostRecentAction.actionId
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching existing actions:", error);
    }
  };

  const fetchFolderStructure = async (siteId) => {
    try {
      const parentFoldersResponse = await get(`/api/document/site/${siteId}/parent/folders`);

      if (parentFoldersResponse?.parentFolders?.length > 0) {
        const logBooksFolder = parentFoldersResponse.parentFolders.find(
            folder => folder.name.trim() === '6 - Log Books'
        );

        if (logBooksFolder) {
          const logBooksResponse = await get(`/api/document/parent/${logBooksFolder.id}/folders?siteId=${siteId}`);

          if (logBooksResponse?.document?.childFolders) {
            const plantAndEquipmentFolder = logBooksResponse.document.childFolders.find(
                folder => folder.name.trim() === 'Electrical Management'
            );

            if (plantAndEquipmentFolder) {
              const plantAndEquipmentResponse = await get(
                  `/api/document/parent/${plantAndEquipmentFolder.id}/folders?siteId=${siteId}`
              );

              if (plantAndEquipmentResponse?.document?.childFolders) {
                const miscellaneousFolder = plantAndEquipmentResponse.document.childFolders.find(
                    folder => folder.name.trim() === 'Security Systems'
                );

                if (miscellaneousFolder) {
                  const miscResponse = await get(
                      `/api/document/parent/${miscellaneousFolder.id}/folders?siteId=${siteId}`
                  );

                  if (miscResponse?.document?.childFolders) {
                    const intruderAlarmFolder = miscResponse.document.childFolders.find(
                        folder => folder.name.trim() === 'Intruder Alarm Service & Maintenance'
                    );

                    setFolderIds({
                      logBooks: logBooksFolder.id,
                      plantAndEquipment: plantAndEquipmentFolder.id,
                      miscellaneousService: miscellaneousFolder.id,
                      intruderAlarmService: intruderAlarmFolder?.id || null
                    });

                    return intruderAlarmFolder?.id || null;
                  }
                }
              }
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching folder structure:', error);
      toast.error('Failed to load document folders');
      return null;
    }
  };

  useEffect(() => {
    const fetchSiteCheckData = async () => {
      try {
        // NEW: Prefer the exact check already loaded by UpdateSiteCheck.
        if (siteCheck && Number(siteCheck.checkId) === Number(checkId)) {
          setCurrentCheckId(siteCheck.checkId);
          setCheckStatus(siteCheck.status);
          setSiteCheckDetails(siteCheck);
          const isDone = siteCheck.status === "Done";
          setIsFormEditable(!isDone);
          setIsSubmitted(isDone);
          setShowPdfButton(isDone);
          return siteCheck;
        }

        if (!authoritativeSiteId) return null;

        const response = await get(`/api/site-check/site/${authoritativeSiteId}`);
        if (response && response.length > 0) {
          let intruderAlarmCheck = checkId
              ? response.find(check => check.checkId === parseInt(checkId, 10))
              : null;

          if (intruderAlarmCheck) {
            setCurrentCheckId(intruderAlarmCheck.checkId);
            setCheckStatus(intruderAlarmCheck.status);
            setSiteCheckDetails(intruderAlarmCheck);

            const isDone = intruderAlarmCheck.status === 'Done';
            setIsFormEditable(!isDone);
            setIsSubmitted(isDone);
            setShowPdfButton(isDone);
            return intruderAlarmCheck;
          } else {
            setCurrentCheckId(checkId ? parseInt(checkId, 10) : null);
            setIsFormEditable(true);
            setIsSubmitted(false);
            setShowPdfButton(false);
          }
        }
      } catch (error) {
        console.error('Error fetching site check data:', error);
        toast.error('Failed to load site check status');
        setIsFormEditable(true);
      }
    };

    if (isInternalUserTaggedWithSite && users.length === 0) {
      getUsers();
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (authoritativeSiteId) {
          await getSiteAssets(authoritativeSiteId);
          await getSiteDetailsById(authoritativeSiteId);
          await fetchFolderStructure(authoritativeSiteId);
          const loadedSiteCheck = await fetchSiteCheckData();
          await fetchInspectionData(
            loadedSiteCheck?.status || siteCheck?.status || checkStatus
          );

          if (formData.actionId) {
            const action = await fetchActionById(formData.actionId);
            if (action) {
              setExistingAction(action);
              setActionRaised(true);
            } else {
              await fetchExistingActions();
            }
          } else {
            await fetchExistingActions();
          }

          const siteData = siteDetailsById || siteSelectedForGlobal;

          if (siteData) {
            const addressParts = [
              siteData.address1,
              siteData.address2,
              siteData.city,
              siteData.area,
              siteData.postCode,
              siteData.country,
            ].filter((part) => part && part.trim() !== "");

            const fullAddress = addressParts.join(", ");
            setFormData((prev) => ({ ...prev, address: fullAddress }));
          }

          if (siteSelectedForGlobal.siteContact) {
            setFormData((prev) => ({
              ...prev,
              siteContact: siteSelectedForGlobal.siteContact.name || "",
              siteContactNo: siteSelectedForGlobal.siteContact.phone || "",
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching site data:", error);
        toast.error("Failed to load site details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    siteSelectedForGlobal,
    getSiteAssets,
    users.length,
    isInternalUserTaggedWithSite,
    getUsers,
    checkId,
  ]);

  useEffect(() => {
    const shouldShowRiskAssessment = [ "param5", "param6", "param7", "param8"].some(
        param => formData[param] === "Fail"
    );
    setShowRiskAssessment(shouldShowRiskAssessment);

    const isActionValid = existingAction && existingAction.checkId === currentCheckId;
    setActionRaised(isActionValid);
  }, [formData.param5, formData.param6, formData.param7, formData.param8, currentCheckId, existingAction]);

  const handleRiskAssessmentComplete = async (actionResponse) => {
    try {
      if (!actionResponse?.actionId) {
        throw new Error("Invalid action response received");
      }

      const verifiedAction = await fetchActionById(actionResponse.actionId);
      if (!verifiedAction || verifiedAction.checkId !== currentCheckId) {
        throw new Error("Action was not properly linked to this inspection");
      }

      setExistingAction(verifiedAction);
      setActionRaised(true);

      setFormData(prev => ({
        ...prev,
        actionId: verifiedAction.actionId
      }));

      if (currentCheckId) {
        const inspectionPayload = {
          address: formData.address,
          assetId: formData.selectedAsset?.assetId || formData.assetId,
          siteContact: formData.siteContactUser?.id || formData.siteContact,
          inspectionDate: formData.inspectionDate,
          siteContactNo: formData.siteContactNo,
          job: formData.job,
          report: formData.report,
          param1: formData.param1,
          param2: formData.param2,
          param3: formData.param3,
          param4: formData.param4,
          param5: formData.param5,
          param6: formData.param6,
          param7: formData.param7,
          param8: formData.param8,

          client: formData.clientUser?.id || formData.client,
          engineer: formData.engineer,
          user: formData.user,
          selectedAsset: formData.selectedAsset,
          signedDate: formData.signedDate,
          clientUser: formData.clientUser,
          siteContactUser: formData.siteContactUser,
          actionId: verifiedAction.actionId,
          checkId: currentCheckId,
          siteId: authoritativeSiteId,
          type: 'Inspection',
          subType: 'Intruder Alarm',
          category: 'Intruder Alarm Service',
        };

        const existingInspections = await get(`/api/site-check/generic-inspection/${currentCheckId}`);
        if (existingInspections?.length > 0) {
          await put(`/api/site-check/generic-inspection/${currentCheckId}`, inspectionPayload);
        } else {
          await post(`/api/site-check/generic-inspection`, inspectionPayload);
        }

        toast.success(`Action #${verifiedAction.actionId} successfully linked to inspection`);
      }
    } catch (error) {
      console.error("Error handling risk assessment completion:", error);
      toast.error(getSiteCheckErrorMessage(error, "Failed to process action completion"));
      setActionRaised(false);
      setExistingAction(null);
      setFormData(prev => ({ ...prev, actionId: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "inspectionDate" ? { signedDate: value } : {}),
    }));
  };

  // NEW: Shared engineer dropdown selection.
  const handleEngineerSelect = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      engineer: newValue?.id || "",
      user: newValue || {},
    }));
    setValidationErrors((prev) => ({ ...prev, engineer: "" }));
  };

  const calculateExpiryDate = (visitDate, repeatFrequency) =>
    calculateSiteCheckDueDate(visitDate, repeatFrequency);

  const savePdfToLocal = async (pdfBlob, fileName) => {
    try {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      return true;
    } catch (error) {
      console.error('Error saving PDF locally:', error);
      return false;
    }
  };

  const getHighestFileVersion = async (folderId, fileName) => {
    try {
      const siteId = authoritativeSiteId;
      if (!siteId) {
        console.warn('No site ID available for file version check');
        return 1;
      }

      const response = await get(`/api/document/parent/${folderId}/folders?siteId=${siteId}`);
      const files = response?.document?.files || [];

      if (files.length > 0) {
        const baseName = fileName.split('.')[0];
        const matchingFiles = files.filter(file =>
            file.name && file.name.startsWith(baseName)
        );

        if (matchingFiles.length > 0) {
          const versions = matchingFiles.map(f => f.fileVersion || 1);
          const maxVersion = Math.max(...versions);
          return maxVersion + 1;
        }
      }
      return 1;
    } catch (error) {
      console.error('Error checking file versions:', error);
      return 1;
    }
  };

  const handleAssetSelect = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      assetId: newValue ? newValue.assetId : "",
      selectedAsset: newValue || null,
    }));
  
      setValidationErrors((prev) => {
        if (!prev.asset) return prev;
        const next = { ...prev };
        delete next.asset;
        return next;
      });
  };

  const checkFileExists = async (folderId, fileName) => {
    try {
      const siteId = authoritativeSiteId;
      if (!siteId || !folderId) return { exists: false, file: null };

      const response = await get(`/api/document/parent/${folderId}/folders?siteId=${siteId}`);
      const files = response?.document?.files || [];
      const baseName = fileName.split('.')[0];
      const existingFile = files.find(file =>
          file.name && file.name.startsWith(baseName)
      );

      return {
        exists: !!existingFile,
        file: existingFile || null
      };
    } catch (error) {
      console.error('Error checking file existence:', error);
      return { exists: false, file: null };
    }
  };

  const dateFormat = (date) => {
    return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
  }

  const uploadPdfToServer = async (pdfBlob, fileName, inspectionDateOverride, checkIdOverride) => {
    let sourceReference = null;
    try {
      setIsUploading(true);
      const inspectionDateForUpload = inspectionDateOverride || formData.inspectionDate;
      const resolvedCheckId = Number(checkIdOverride ?? currentCheckId);
      if (!Number.isInteger(resolvedCheckId) || resolvedCheckId <= 0) {
        throw new Error('Could not determine Site Check ID for PDF upload');
      }
      sourceReference = `IntruderAlarm-${resolvedCheckId}-${Date.now()}`;
      const savedLocally = await savePdfToLocal(pdfBlob, fileName);
      if (!savedLocally) {
        throw new Error('Failed to save PDF locally');
      }

      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const targetFolderId = folderIds.intruderAlarmService || folderIds.logBooks;

      if (!targetFolderId) {
        throw new Error('Could not determine target folder for PDF upload');
      }

      const { exists, file: existingFile } = await checkFileExists(targetFolderId, fileName);

      // OLD: const formData = new FormData();
      // NEW: avoid hiding the React formData state.
      const uploadFormData = new FormData();

      if (exists && existingFile) {
        uploadFormData.append('file', pdfFile);
        const documentRequestString = {
          folderId: targetFolderId,
          files: [{
            id: existingFile.id,
            name: fileName,
            originalFileName: fileName,
            fileVersion: existingFile.fileVersion + 1,
            siteId: authoritativeSiteId || 0,
            issueDate: toJavaLocalDateTime(inspectionDateForUpload),
            expiryDate: toJavaLocalDateTime(calculateExpiryDate(inspectionDateForUpload, siteCheckDetails?.repeatFrequency)),
            uploaderUserId: loggedInUserData?.id || 0,
            reviewerUserId: loggedInUserData?.id || 0,
            referenceNumber: sourceReference
          }]
        };

        uploadFormData.append('documentRequestString', JSON.stringify(documentRequestString));
        const response = await axios({
          method: 'put',
          url: '/api/document/file/newVersion/upload',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json'
          }
        });

        if (response?.status >= 200 && response?.status < 300) {
          toast.success(`PDF uploaded successfully as version ${documentRequestString.fileVersion}!`);
          return { stored: true, sourceReference };
        }
      } else {
        uploadFormData.append('files', pdfFile);
        const fileVersion = await getHighestFileVersion(targetFolderId, fileName);

        const documentRequestString = {
          folderId: targetFolderId,
          files: [{
            name: fileName.split('.')[0],
            issueDate: toJavaLocalDateTime(inspectionDateForUpload),
            expiryDate: toJavaLocalDateTime(calculateExpiryDate(inspectionDateForUpload, siteCheckDetails?.repeatFrequency)),
            note: 'Intruder Alarm Service Certificate',
            fileVersion: fileVersion,
            siteId: authoritativeSiteId || 0,
            originalFileName: fileName,
            uploaderUserId: loggedInUserData?.id || 0,
            reviewerUserId: loggedInUserData?.id || 0,
            referenceNumber: sourceReference
          }]
        };

        uploadFormData.append('documentRequestString', JSON.stringify(documentRequestString));
        const response = await axios({
          method: 'post',
          url: '/api/document/files/upload',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response?.status >= 200 && response?.status < 300) {
          toast.success(`PDF uploaded successfully as version ${fileVersion}!`);
          return { stored: true, sourceReference };
        }
      }

      throw new Error('Upload failed: the Site Document API did not confirm success');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      return { stored: false, sourceReference: null };
    } finally {
      setIsUploading(false);
    }
  };

  const generatePDF = async (uploadToServer = true, inspectionDateOverride, checkIdOverride) => {
    try {
      setIsGeneratingPDF(true);

      if (!PDFLib) {
        PDFLib = await import('pdf-lib');
      }

      const pdfBytes = await fetchPdfTemplate();
      const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes);
      const helveticaFont = await pdfDoc.embedFont(PDFLib.StandardFonts.Helvetica);
      const form = pdfDoc.getForm();

      // Debug: Log all field names
      console.log('PDF Form Fields:');
      form.getFields().forEach((field, i) => {
        try {
          const name = field.getName();
          const type = field.constructor.name;
          console.log(`${i + 1}. ${name} (${type})`);
        } catch (error) {
          console.warn('Error getting field info:', error);
        }
      });

      const setTextField = (fieldName, value, fontSize = 5) => {
        try {
          const field = form.getTextField(fieldName);
          if (field) {
            field.setText(value || '');
            try {
              if (field.setFontSize) {
                field.updateAppearances(helveticaFont);
                field.setFontSize(fontSize);
              }
            } catch (e) {
              console.warn(`Could not set font size for ${fieldName}:`, e);
            }
          }
        } catch (error) {
          console.warn(`Error setting text field ${fieldName}:`, error);
        }
      };

      const setCheckbox = (fieldName, isChecked) => {
        try {
          const field = form.getCheckBox(fieldName);
          if (field) {
            field.check(isChecked);
          }
        } catch (error) {
          console.warn(`Error setting checkbox ${fieldName}:`, error);
        }
      };

      const smallFont = 8;
      const mediumFont = 10;
      const effectiveInspectionDate = inspectionDateOverride || formData.inspectionDate;

      // Address and contact information
      const addressLines = (formData.address || '').split(',');
      setTextField('Address', addressLines[0] || '', smallFont);
      setTextField('Address_2', addressLines[1] || '', smallFont);
      setTextField('Address_3', addressLines[2] || '', smallFont);
      setTextField('Address_4', addressLines[3] || '', smallFont);

      // OLD: setTextField('Date', dateFormat(formData.inspectionDate), smallFont);
      // NEW: exact UK submission date.
      setTextField('Date', dateFormat(effectiveInspectionDate), smallFont);
      setTextField('Site Contact', formData.siteContactUser?.name || '', smallFont);
      setTextField('Site Contact No', formData.siteContactNo || '', smallFont);
      setTextField('Job No', formData.job || '', smallFont);

      const equipmentDetailsLocation = [
        selectedAsset?.floor,
        selectedAsset?.room,
        selectedAsset?.position,
        selectedAsset?.assetName
      ].filter(Boolean).join(' - ');

      // Equipment information
      setTextField('Manufacturer', selectedAsset?.manufacturer || '', smallFont);
      setTextField('Model Number', selectedAsset?.model || '', smallFont);
      setTextField('Panel Details', equipmentDetailsLocation, smallFont);

      // Test results
      setTextField('Job Complete', formData.param1 === 'Pass' ? 'Yes' : 'No', smallFont);
      setTextField('Parts Required', formData.param2 === 'Pass' ? 'Yes' : 'No', smallFont);
      setTextField('Walk Test', formData.param3 === 'Pass' ? 'Yes' : 'No', smallFont);
      setTextField('PIRsCleaned', formData.param4 === 'Pass' ? 'Yes' : 'No', smallFont);
      setTextField('Remote', formData.param5 === 'Pass' ? 'Pass' : 'Fail', smallFont);
      setTextField('System Check', formData.param6 === 'Pass' ? 'Pass' : 'Fail', smallFont);
      setTextField('Audible Check', formData.param7 === 'Pass' ? 'Pass' : 'Fail', smallFont);
      setTextField('Electrical Check', formData.param8 === 'Pass' ? 'Pass' : 'Fail', smallFont);


      // Report
      setTextField('Engineers Report', formData.report || '', smallFont);

      // Signatures
      const clientName = formData.clientUser?.name || formData.client || '';
      const engineerName = formData.user?.name || '';

      setTextField('Clients Name', clientName, smallFont);
      setTextField('Engineers Name', engineerName, smallFont);
      // OLD signature-date mapping retained for review.
      // setTextField('on', dateFormat(formData.signedDate), smallFont);
      // setTextField('on_2', dateFormat(formData.signedDate), smallFont);
      // NEW: exact UK submission date.
      setTextField('on', dateFormat(formData.signedDate), smallFont);
      setTextField('on_2', dateFormat(formData.signedDate), smallFont);

      // Flatten and save
      form.flatten();
      const pdfBytesModified = await pdfDoc.save();
      const blob = new Blob([pdfBytesModified], { type: 'application/pdf' });
      const fileName = `IntruderAlarmServiceCertificate_${formData.selectedAsset?.assetName || 'report'}.pdf`;

      setGeneratedPdfBlob(blob);
      setShowPdfButton(true);

      let uploadResult = { stored: false, sourceReference: null };
      if (uploadToServer) {
        uploadResult = await uploadPdfToServer(
          blob, fileName, effectiveInspectionDate, checkIdOverride
        );
        if (!uploadResult?.stored) {
          throw new Error('PDF was generated but could not be stored in Site Documents');
        }
      }

      toast.success('PDF generated successfully!');
      return { success: true, fileName, uploadResult };

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
      return { success: false, error: error.message };
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  useEffect(() => {
    const fetchActionData = async () => {
      if (formData.actionId) {
        console.log('Action ID changed, fetching action:', formData.actionId);
        const action = await fetchActionById(formData.actionId);
        if (action) {
          setExistingAction(action);
          setActionRaised(true);
        } else {
          setExistingAction(null);
          setActionRaised(false);
        }
      }
    };

    fetchActionData();
  }, [formData.actionId]);

  const retryPendingHistory = async () => {
      if (!pendingHistoryRetry || isRetryingHistory) return;

      setIsRetryingHistory(true);
      try {
        const history = await recordGenericInspectionHistory(pendingHistoryRetry);
        setPendingHistoryRetry(null);
        toast.success(`History recorded successfully (History #${history.historyId}).`);
      } catch (error) {
        const message = getSiteCheckErrorMessage(error, "History record could not be created.");
        console.error("Retry Generic Inspection history:", { ...pendingHistoryRetry, status: error?.response?.status, message, error });
        toast.error(`History retry failed: ${message}`);
      } finally {
        setIsRetryingHistory(false);
      }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted');

    if (isLoading) {
      console.log('Submit prevented: Already loading');
      return;
    }

    // Validation checks
    const hasFailures = [

      formData.param5,
      formData.param6,
      formData.param7,
      formData.param8
    ].some(val => val === "Fail");

    if (hasFailures && !actionRaised) {
      toast.error("Please complete the risk assessment before submitting");
      return;
    }

    if (!isFormEditable) {
      console.log('Submit prevented: Form is not editable');
      return;
    }

    // Form validation
    const errors = {};
    if (!formData.assetId || !(selectedAsset || formData.selectedAsset)) {
      errors.asset = "Please select an Intruder Alarm Device.";
    }
    if (!formData.engineer || !selectedEngineer) {
      errors.engineer = "Please select an active engineer for this Site Check.";
    }
    if (!formData.param1) errors.param1 = "Please select one option";
    if (!formData.param2) errors.param2 = "Please select one option";
    if (!formData.param3) errors.param3 = "Please select one option";
    if (!formData.param4) errors.param4 = "Please select one option";
    if (!formData.param5) errors.param5 = "Please select one option";
    if (!formData.param6) errors.param6 = "Please select one option";
    if (!formData.param7) errors.param7 = "Please select one option";
    if (!formData.param8) errors.param8 = "Please select one option";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (errors.engineer) toast.error(errors.engineer);
      else if (errors.asset) toast.error(errors.asset);
      return;
    }

    setValidationErrors({});
    setIsLoading(true);

    try {
      // NEW: Open checks complete using today's UK date, matching Air Conditioning.
      const submissionInspectionDate = formData.inspectionDate;
      const submissionSignedDate = formData.signedDate;

      // First check if we have an existing inspection
      let existingInspection = null;
      if (currentCheckId) {
        try {
          const inspections = await get(`/api/site-check/generic-inspection/${currentCheckId}`);
          existingInspection = inspections?.length > 0 ? inspections[0] : null;
        } catch (error) {
          console.error('Error checking for existing inspection:', error);
        }
      }

      // First update or create the site check status
      const statusPayload = {
        siteId: parseInt(authoritativeSiteId, 10),
        type: siteCheck?.type || 'Inspection',
        // OLD: subType: 'Intruder Alarm', category: 'Intruder Alarm Service'
        // NEW: preserve the routing values of the Site Check.
        subType: siteCheck?.subType || 'Intruder Alarm',
        category: siteCheck?.category || 'Intruder Alarm Servicing & Inspection',
        status: 'Done',
        startDate: toJavaLocalDateTime(submissionInspectionDate),
        dueDate: toJavaLocalDateTime(calculateExpiryDate(submissionInspectionDate, siteCheckDetails?.repeatFrequency)),
        leadUserID: loggedInUserData?.id ? String(loggedInUserData.id) : '0',
        assistantUserID: loggedInUserData?.id ? String(loggedInUserData.id) : '0'
      };

      let statusResponse;
      if (currentCheckId) {
        // Update existing check
        statusPayload.checkId = parseInt(currentCheckId, 10);
        statusResponse = await put(
            `/api/site-check/${currentCheckId}/completion`,
            statusPayload
        );
      } else {
        // Create new check
        statusResponse = await post(
            `/api/site-check`,
            statusPayload
        );
        if (statusResponse?.checkId) {
          setCurrentCheckId(statusResponse.checkId);
        }
      }

      if (![200, 201, 204].includes(statusResponse?.status)) {
        throw new Error('Failed to update site check status');
      }

      console.log('Site check status updated successfully:', statusResponse.data);
      setCheckStatus('Done');
      setIsFormEditable(false);

      // Then update or create the generic inspection record
      const inspectionPayload = {
        ...formData,
        siteId: authoritativeSiteId,
        assetId: formData.selectedAsset?.assetId || formData.assetId,
        client: formData.clientUser?.id || formData.client,
        engineer: formData.engineer,
        inspectionDate: toJavaLocalDate(submissionInspectionDate),
        signedDate: toJavaLocalDate(submissionSignedDate),
        siteContact: formData.siteContactUser?.id || formData.siteContact,
        type: 'Inspection',
        subType: 'Intruder Alarm',
        category: 'Intruder Alarm Service',
        checkId: currentCheckId || statusResponse?.checkId,
        actionId: formData.actionId,
      };

      let saveResponse;
      if (existingInspection) {
        // Update existing inspection
        saveResponse = await put(
            `/api/site-check/generic-inspection/${currentCheckId}`,
            inspectionPayload
        );
      } else {
        // Create new inspection
        saveResponse = await post(
            `/api/site-check/generic-inspection`,
            inspectionPayload
        );
      }

      if (![200, 201, 204].includes(saveResponse?.status)) {
        throw new Error('Failed to save inspection data');
      }

      console.log('Inspection data saved successfully:', saveResponse.data);

      const resolvedCheckIdForHistory = Number(
        currentCheckId || statusResponse?.data?.checkId || statusResponse?.checkId
      );
      const savedInspectionRecordId = Number(saveResponse?.data?.id);

      const pdfResult = await generatePDF(true, submissionInspectionDate, resolvedCheckIdForHistory);
      if (!pdfResult.success) {
        throw new Error(pdfResult.error || "Failed to generate PDF");
      }

      const sourceReference = String(pdfResult?.uploadResult?.sourceReference || "").trim();
      if (!Number.isInteger(resolvedCheckIdForHistory) || resolvedCheckIdForHistory <= 0) {
        throw new Error("Intruder Alarm Service was saved, but History cannot be recorded because the Site Check ID is invalid.");
      }
      if (!Number.isInteger(savedInspectionRecordId) || savedInspectionRecordId <= 0) {
        throw new Error("Intruder Alarm Service was saved, but History cannot be recorded because the saved inspection record ID was not returned.");
      }
      if (!pdfResult?.uploadResult?.stored) {
        throw new Error("Intruder Alarm Service was saved, but History cannot be recorded because the PDF was not stored in Site Documents.");
      }
      if (!sourceReference) {
        throw new Error("Intruder Alarm Service was saved, but History cannot be recorded because the PDF reference was not returned.");
      }

      const historyPayload = { checkId: resolvedCheckIdForHistory, inspectionRecordId: savedInspectionRecordId, sourceReference };
      try {
        await recordGenericInspectionHistory(historyPayload);
        setPendingHistoryRetry(null);
      } catch (historyError) {
        const historyMessage = getSiteCheckErrorMessage(historyError, "History record could not be created.");
        console.error("Record Intruder Alarm Service history:", { ...historyPayload, status: historyError?.response?.status, message: historyMessage, error: historyError });
        setPendingHistoryRetry(historyPayload);
        setShowPdfButton(true);
        setIsSubmitted(true);
        setSubmissionSuccess(true);
        toast.error(`Report and PDF were saved, but History was not recorded: ${historyMessage}`);
        return;
      }

      toast.success("Intruder Alarm Service report saved, PDF generated, and History recorded successfully!");
      setShowPdfButton(true);
      setIsSubmitted(true);
      setSubmissionSuccess(true);

      setTimeout(() => {
        if (!embedded) {
          navigate(-1);
        }
      }, 1500);

    } catch (error) {
      console.error('Error in form submission:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(getSiteCheckErrorMessage(error, "Failed to submit form"));
    } finally {
      setIsLoading(false);
    }
  };

  const renderClientNameField = () => {
    if (isInternalUserTaggedWithSite) {
      const filteredUsers =
          users?.filter((user) =>
              user.taggedSites?.some(
                  (site) => site.id === authoritativeSiteId
              )
          ) || [];

      return (
          <Autocomplete
              options={filteredUsers}
              getOptionLabel={(user) => user.name}
              value={formData.clientUser || formData.siteContactUser || null}
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  client: newValue?.id || "",
                  clientUser: newValue || null,
                  siteContact: newValue?.id || "",
                  siteContactNo: newValue?.phone || "",
                  siteContactUser: newValue || null,
                }));
              }}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      variant="outlined"
                      required
                      disabled={isSubmitted}
                      style={{
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "8.5px 4px !important",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          padding: "0 5px",
                        },
                      }}
                  />
              )}
              disabled={isSubmitted}
          />
      );
    }
    return (
        <input
            type="text"
            className="form-control"
            name="clientName"
            value={
                formData.clientUser?.name || formData.siteContactUser?.name || ""
            }
            onChange={(e) => {
              setFormData({
                ...formData,
                client: e.target.value,
                clientNameText: e.target.value,
                siteContact: e.target.value,
                siteContactName: e.target.value,
              });
            }}
            required
            disabled={isSubmitted}
        />
    );
  };

  const renderSiteContactField = () => {
    if (isInternalUserTaggedWithSite) {
      const filteredUsers =
          users?.filter((user) =>
              user.taggedSites?.some(
                  (site) => site.id === authoritativeSiteId
              )
          ) || [];

      return (
          <Autocomplete
              options={filteredUsers}
              getOptionLabel={(user) => user.name}
              value={formData.siteContactUser || formData.clientUser || null}
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  siteContact: newValue?.id || "",
                  siteContactNo: newValue?.phone || "",
                  siteContactUser: newValue || null,
                  client: newValue?.id || "",
                  clientUser: newValue || null,
                }));
              }}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      variant="outlined"
                      required
                      disabled={isSubmitted}
                      style={{
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "8.5px 4px !important",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          padding: "0 5px",
                        },
                      }}
                  />
              )}
              disabled={isSubmitted}
          />
      );
    }
    return (
        <input
            type="text"
            className="form-control"
            name="siteContact"
            value={
                formData.siteContactUser?.name || formData.clientUser?.name || ""
            }
            onChange={(e) => {
              setFormData({
                ...formData,
                siteContact: e.target.value,
                siteContactName: e.target.value,
                client: e.target.value,
                clientNameText: e.target.value,
              });
            }}
            required
            disabled={isSubmitted}
        />
    );
  };

  const filteredAssets =
      siteAssets?.filter(
          (asset) =>
              asset.category === "Electrical" &&
              asset.subCategory === "Intruder Alarm Installation"
      ) || [];

  return (
      <div className="container mt-4 mb-5">
        <div className="header text-center bg-light p-4 mb-4 rounded d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Intruder Alarm Service Report</h4>
        </div>
        {!isFormEditable && (
            <div className="alert alert-warning" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              This form is read-only because the check has been marked as completed.
            </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="mb-3 d-flex">
                <label
                    className="form-label"
                    style={{ fontWeight: "bold", marginRight: "20px" }}
                >
                  Address
                </label>
                <textarea
                    className="form-control"
                    rows={3}
                    name="address"
                    value={formData.address || ""}
                    disabled
                    style={{
                      width: "300px",
                      height: "150px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "normal",
                      fontSize: "15px",
                    }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="mb-3">
                <label className="form-label">Inspection Date</label>
                <input
                    type="date"
                    className="form-control"
                    name="inspectionDate"
                    value={formatDate(formData.inspectionDate)}
                    onChange={handleInputChange}
                    required
                    style={{
                      height: "40px",
                      padding: "0 10px",
                      width: "100%",
                    }}
                    disabled={isSubmitted}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Site Contact</label>
                {renderSiteContactField()}
              </div>
            </div>
            <div className="col-md-3">
              <div className="mb-3">
                <label className="form-label">Site Contact No.</label>
                <input
                    type="text"
                    className="form-control"
                    name="siteContactNo"
                    value={formData.siteContactNo}
                    onChange={handleInputChange}
                    disabled={isSubmitted}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Job No.</label>
                <input
                    type="text"
                    className="form-control"
                    name="job"
                    value={formData.job}
                    onChange={handleInputChange}
                    disabled={isSubmitted}
                />
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Device Information</h5>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-12">
                  <Autocomplete
                      disabled={isSubmitted}
                      options={filteredAssets}
                      getOptionLabel={(option) =>
                          `${option.assetId} - ${option.assetName} (${
                              option.position || "NA"
                          } > ${option.floor || "NA"} > ${option.room || "NA"})`
                      }
                      value={selectedAsset}
                      onChange={handleAssetSelect}
                      renderInput={(params) => (
                          <TextField
                              {...params}
                              label="Select an Intruder Alarm Device"
                              variant="outlined"
                              placeholder="Search devices..."
                              error={Boolean(validationErrors.asset)}
                              helperText={validationErrors.asset || ""}
                          />
                      )}
                      sx={{ width: "100%" }}
                  />
                </div>
              </div>

              {formData.selectedAsset && (
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Panel Manufacturer</label>
                        <input
                            type="text"
                            className="form-control"
                            name="manufacturer"
                            value={selectedAsset?.manufacturer || ""}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Panel Model Number</label>
                        <input
                            type="text"
                            className="form-control"
                            name="model"
                            value={selectedAsset?.model || ""}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Position</label>
                        <input
                            type="text"
                            className="form-control"
                            name="position"
                            value={selectedAsset?.position || ""}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Floor</label>
                        <input
                            type="text"
                            className="form-control"
                            name="floor"
                            value={selectedAsset?.floor || ""}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Room</label>
                        <input
                            type="text"
                            className="form-control"
                            name="room"
                            value={selectedAsset?.room || ""}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                  </div>
              )}
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Engineers Report</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <TextField
                    multiline
                    rows={16}
                    fullWidth
                    variant="outlined"
                    value={formData.report || ""}
                    onChange={(e) =>
                        setFormData({
                          ...formData,
                          report: e.target.value,
                        })
                    }
                    style={{ height: "400px" }}
                    disabled={isSubmitted}
                />
              </div>
            </div>
          </div>

          {popup.show && (
              <div
                  className="remark-popup"
                  style={{
                    position: "fixed",
                    left: `${popup.position.x}px`,
                    top: `${popup.position.y}px`,
                    transform: "translateY(-100%)",
                    zIndex: 1000,
                    maxWidth: "400px",
                    padding: "10px",
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    wordBreak: "break-word",
                  }}
              >
                {popup.content}
              </div>
          )}

          <div className="mb-4">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <tbody>
                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">Job Complete</label>
                        <select
                            className={`form-select ${
                                validationErrors.param1 ? "is-invalid" : ""
                            }`}
                            value={formData.param1}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param1: e.target.value,
                              });
                              if (validationErrors.param1) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param1;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Yes</option>
                          <option value="Fail">No</option>
                        </select>
                        {validationErrors.param1 && (
                            <div className="invalid-feedback">
                              {validationErrors.param1}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">Parts Required</label>
                        <select
                            className={`form-select ${
                                validationErrors.param2 ? "is-invalid" : ""
                            }`}
                            value={formData.param2}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param2: e.target.value,
                              });
                              if (validationErrors.param2) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param2;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Yes</option>
                          <option value="Fail">No</option>
                        </select>
                        {validationErrors.param2 && (
                            <div className="invalid-feedback">
                              {validationErrors.param2}
                            </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <tbody>
                  <tr style={{ fontSize: "18px" }}>
                    <td
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          width: "400px",
                        }}
                    >
                      Service Items Undertaken
                    </td>
                    <td
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          width: "400px",
                        }}
                    >
                    </td>
                  </tr>

                  <tr style={{ fontSize: "22px" }}>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">Walk Test Complete</label>
                        <select
                            className={`form-select ${
                                validationErrors.param3 ? "is-invalid" : ""
                            }`}
                            value={formData.param3}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param3: e.target.value,
                              });
                              if (validationErrors.param3) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param3;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Yes</option>
                          <option value="Fail">No</option>
                        </select>
                        {validationErrors.param3 && (
                            <div className="invalid-feedback">
                              {validationErrors.param3}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">PIR's Cleaned</label>
                        <select
                            className={`form-select ${
                                validationErrors.param4 ? "is-invalid" : ""
                            }`}
                            value={formData.param4}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param4: e.target.value,
                              });
                              if (validationErrors.param4) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param4;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Yes</option>
                          <option value="Fail">No</option>
                        </select>
                        {validationErrors.param4 && (
                            <div className="invalid-feedback">
                              {validationErrors.param4}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">
                          Remote signalling equipment check
                        </label>
                        <select
                            className={`form-select ${
                                validationErrors.param5 ? "is-invalid" : ""
                            }`}
                            value={formData.param5}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param5: e.target.value,
                              });
                              if (validationErrors.param5) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param5;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                        </select>
                        {validationErrors.param5 && (
                            <div className="invalid-feedback">
                              {validationErrors.param5}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">
                          System Operation Check
                        </label>
                        <select
                            className={`form-select ${
                                validationErrors.param6 ? "is-invalid" : ""
                            }`}
                            value={formData.param6}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param6: e.target.value,
                              });
                              if (validationErrors.param6) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param6;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                        </select>
                        {validationErrors.param6 && (
                            <div className="invalid-feedback">
                              {validationErrors.param6}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">
                          Audible warning and alarm devices Check
                        </label>
                        <select
                            className={`form-select ${
                                validationErrors.param7 ? "is-invalid" : ""
                            }`}
                            value={formData.param7}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param7: e.target.value,
                              });
                              if (validationErrors.param7) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param7;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                        </select>
                        {validationErrors.param7 && (
                            <div className="invalid-feedback">
                              {validationErrors.param7}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div className="mb-3">
                        <label className="form-label">
                          Electrical Connections Check
                        </label>
                        <select
                            className={`form-select ${
                                validationErrors.param8 ? "is-invalid" : ""
                            }`}
                            value={formData.param8}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                param8: e.target.value,
                              });
                              if (validationErrors.param8) {
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.param8;
                                  return newErrors;
                                });
                              }
                            }}
                            disabled={isSubmitted}
                        >
                          <option value="">Select</option>
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                        </select>
                        {validationErrors.param8 && (
                            <div className="invalid-feedback">
                              {validationErrors.param8}
                            </div>
                        )}
                      </div>
                    </td>
                    <td>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {showRiskAssessment && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Risk Assessment</h5>
                  {existingAction?.checkId === currentCheckId && (
                      <span className="badge bg-success ms-2">
                  Action #{existingAction.actionId} - {existingAction.status}
                </span>
                  )}
                </div>
                <div className="card-body">
                  {existingAction?.checkId === currentCheckId ? (
                      <div className="existing-action">
                        <div className="row">
                          <div className="col-md-6">
                            <p><strong>Observation:</strong> {existingAction.observation}</p>
                            <p><strong>Required Action:</strong> {existingAction.requiredAction}</p>
                            <p><strong>Risk Score:</strong> {existingAction.riskScore}</p>
                          </div>
                          <div className="col-md-6">
                            <p><strong>Description: </strong> {existingAction.desc}</p>
                            <p><strong>Due Date:</strong> {formatDate(existingAction.dueDate)}</p>
                            <p><strong>Status:</strong> {existingAction.status}</p>
                          </div>
                        </div>
                        {existingAction.comments && (
                            <div className="mt-3">
                              <h6>Comments:</h6>
                              <p>{existingAction.comments}</p>
                            </div>
                        )}
                      </div>
                  ) : (
                      <RiskScoreCard
                          desc={`Inspection - Intruder Alarm System Service`}
                          siteId={authoritativeSiteId}
                          checkId={currentCheckId}
                          createdBy={loggedInUserData?.id}
                          taggedAsset={selectedAsset?.assetId}
                          onRiskAssessmentComplete={handleRiskAssessmentComplete}
                          actionRaised={actionRaised}
                          disabled={isSubmitted}
                      />
                  )}
                </div>
              </div>
          )}

          <div className="row mt-4">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Client's Name</label>
                {renderClientNameField()}
              </div>

              <div className="mb-3">
                <label className="form-label">Signed Date</label>
                <input
                    type="date"
                    className="form-control"
                    name="signedDate"
                    value={formatDate(formData.inspectionDate || formData.signedDate)}
                    readOnly
                    required
                    style={{
                      height: "40px",
                      padding: "0 10px",
                      width: "100%",
                      backgroundColor: "#f8f9fa",
                    }}
                />
              </div>
            </div>
            <div className="col-md-6">
              {/* =========================================================
                  OLD ENGINEER FIELD - COMMENTED FOR REVIEW

              <div className="mb-3">
                <label className="form-label fw-bold">Engineer's Name</label>
                <input
                    type="text"
                    className="form-control"
                    name="engineer name"
                    readOnly
                    value={formData.user.name}
                    required
                    disabled
                />
              </div>

              ========================================================= */}

              {/* NEW SHARED ENGINEER CONTROL - MATCHES AIR CONDITIONING */}
              <SiteCheckEngineerSelector
                options={engineerOptions}
                value={selectedEngineer}
                onChange={handleEngineerSelect}
                isOpen={checkStatus === "Open"}
                disabled={isSubmitted || !isFormEditable}
                loading={isLoadingEngineers}
                error={validationErrors.engineer || engineerLoadError}
              />
              <SiteCheckEngineerSignature
                  engineer={selectedEngineer}
                  engineerId={formData.engineer || formData.user?.id}
                  fallbackSignature={formData.user?.signature || ""}
                  sasToken={sasToken}
              />
            </div>
          </div>

          <div className="mt-4 print-hide">
            {/* SiteCheckPersistentSubmittedBack: keep navigation available after submission. */}
          {isSubmitted && (
            <div className="mt-3 print-hide">
              <SiteCheckBackButton />
            </div>
          )}

          {!isSubmitted ? (
                <div className="d-flex justify-content-between mt-3">
                  <SiteCheckBackButton />
                  <div>
                    {isFormEditable && (
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={
                                isLoading ||
                                isGeneratingPDF ||
                                (showRiskAssessment && !actionRaised)
                            }
                        >
                          {isLoading ? 'Submitting...' : 'Submit Report'}
                        </button>
                    )}
                  </div>
                </div>
            ) : (
                <div className="text-center">
                  {pendingHistoryRetry ? (
                    <div className="alert alert-warning mb-4">
                      <div className="fw-bold mb-2">Report and PDF saved, but History has not been recorded.</div>
                      <div className="mb-3">Do not submit the inspection again. Retry only the History record below.</div>
                      <button type="button" className="btn btn-warning" disabled={isRetryingHistory} onClick={retryPendingHistory}>
                        {isRetryingHistory ? "Retrying History..." : "Retry History"}
                      </button>
                    </div>
                  ) : (
                    <div className="alert alert-success mb-4">
                      Report submitted successfully on {getUkLocalDate()}
                    </div>
                  )}
                </div>
            )}
          </div>
        {!isSubmitted && (
          <div className="d-flex justify-content-end print-hide">
            <SiteCheckDueSummary
              inspectionDate={formData.inspectionDate}
              repeatFrequency={siteCheck?.repeatFrequency || siteCheckDetails?.repeatFrequency}
            />
          </div>
        )}
        </form>

        <style>{`
        .is-invalid {
          border-color: #dc3545 !important;
        }
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: .25rem;
          font-size: .875em;
          color: #dc3545;
        }
        @media print {
          .print-hide {
            display: none !important;
          }
          body {
            padding: 0;
            margin: 0;
          }
          .container {
            max-width: 100%;
            padding: 0;
          }
        }
      `}</style>
      </div>
  );
};

const mapStateToProps = (state) => ({
  sites: state.site.sites,
  users: state.site.users,
  siteAssets: state.site.siteAssets,
  siteDetailsById: state.site.siteDetailsById,
  siteSelectedForGlobal: state.site.siteSelectedForGlobal,
  loggedInUserData: state.site.loggedInUserData,
});

export default connect(mapStateToProps, {
  getSiteDetailsById,
  getSiteAssets,
  getSites,
  getUsers,
})(IntruderAlarmCertificate);
